import React, { useCallback, useEffect, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material//CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material//CheckBoxOutlineBlank';
import { Autocomplete, Checkbox, Stack, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { DocumentReferencesView } from 'api';
import { FILE_NAME } from 'common/constants/documents';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { NotReferencedDocumentsListProps } from './NotReferencedDocumentsList.props';

const NotReferencedDocumentsList = ({
  documents,
  placeholder,
  referencedDocuments,
  setSelectedValues,
}: NotReferencedDocumentsListProps) => {
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentReferencesView[]>([]);
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleChange = (_event: React.ChangeEvent<unknown>, data: DocumentReferencesView[]) => {
    setSelectedValues(data);
  };

  const isExportedDocument = useCallback(
    (document: DocumentReferencesView) => {
      const companyName = companyInfo?.name;
      if (!companyName) {
        return false;
      }
      const fileName = FILE_NAME(document);
      // Check if the filename starts with the company name followed by an underscore
      return fileName.startsWith(`${companyName}_`);
    },
    [companyInfo?.name]
  );

  useEffect(() => {
    const filteredReferencedDocuments = documents.filter(
      document =>
        !referencedDocuments?.map(refDoc => refDoc.document_reference_id).includes(document.document_reference_id) &&
        !isExportedDocument(document)
    );
    // Add selected attribute for each document in filteredReferencedDocuments
    const selectedDocuments = filteredReferencedDocuments.map(document => ({
      ...document,
    }));
    setFilteredDocuments(selectedDocuments);
  }, [documents, referencedDocuments, isExportedDocument]);

  const getFileName = useCallback((option: DocumentReferencesView) => {
    if (option.file) {
      return FILE_NAME(option);
    }
    return '';
  }, []);

  return (
    <div style={{ width: '100%' }}>
      {documents && (
        <Autocomplete
          multiple
          id="checkboxes-documents"
          options={filteredDocuments}
          disableCloseOnSelect
          onChange={handleChange}
          getOptionLabel={getFileName}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
              <Stack direction="row" spacing={1} alignItems="baseline">
                <span>{getFileName(option)}</span>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                  {`Uploaded on ${moment(option.file.uploaded_date).format('MMM D, YYYY')}`}
                </Typography>
              </Stack>
            </li>
          )}
          renderInput={params => <TextField {...params} variant="outlined" placeholder={placeholder} />}
          style={{ width: '100%' }}
          isOptionEqualToValue={(option, value) => option.document_reference_id === value.document_reference_id}
        />
      )}
    </div>
  );
};

export default NotReferencedDocumentsList;
